import { Popover, Transition } from '@headlessui/react';
import Icon, { IconName } from '../Icon';
import TooltipV2 from '../../TooltipV2';
import { ACTION_BUTTON_SIZE_CLASSES } from './constants';
import { classnames } from '@/libs/utils';
import { CLIP_CUSTOMIZER_WARNING } from '@/Pages/Clip/ClipPlayer/ClipCustomizer/useClipCustomizerWarnings';
import { useAppContext } from '@/context/AppContext/AppContext';

export type ActionButton = {
  action: () => void | Promise<void>;
  label: string;
  icon: IconName;
  trackingId: string;
  disabled: boolean;
  isActive?: boolean;
  hidden?: boolean;
  isLoading?: boolean;
  isLocked?: boolean;
  tooltip?: string;
  buttonRef?: React.RefObject<HTMLButtonElement>;
  isBetaFeature?: boolean;
} & CLIP_CUSTOMIZER_WARNING;

export default function ActionButtons({
  show,
  buttons,
  labelPosition,
  size
}: {
  show: boolean;
  buttons: ActionButton[];
  labelPosition: 'left' | 'right';
  size: 'base' | 'medium' | 'large';
}) {
  const { theme: appTheme } = useAppContext();
  const theme = appTheme || 'goldcast';
  return (
    <div className="flex items-center justify-center">
      <Transition
        show={show}
        enter="transition duration-150 ease-out"
        enterFrom="transform translate-x-2 scale-[0.98] opacity-0"
        enterTo="transform translate-x-0 scale-1 opacity-100"
        leave="transition duration-100 ease-out"
        leaveFrom="transform translate-x-0 scale-1 opacity-100"
        leaveTo="transform translate-x-2 scale-[0.98] opacity-0"
      >
        <div className="relative z-50 flex flex-col rounded-xl">
          <Popover
            className={classnames('flex flex-col overflow-y-auto', {
              'gap-y-1': (theme === 'goldcast' && size === 'medium') || size === 'large',
              'p-1': size === 'medium',
              'p-1.5': size === 'large'
            })}
          >
            {({ open }) => (
              <>
                {buttons.map(
                  ({
                    action,
                    label,
                    icon,
                    trackingId,
                    disabled,
                    isActive,
                    hidden,
                    isLoading,
                    isLocked,
                    tooltip,
                    buttonRef,
                    isBetaFeature,
                    showWarning
                  }) => {
                    if (hidden) {
                      return null;
                    }

                    const buttonElement = (
                      <button
                        key={trackingId}
                        ref={buttonRef}
                        data-testid={trackingId}
                        onClick={action}
                        disabled={disabled}
                        className={classnames('group relative flex flex-col items-center justify-center rounded-lg', {
                          'size-10': size === 'base',
                          'w-16 p-2': size === 'medium',
                          'w-20 p-3': size === 'large',
                          'bg-slate-200 text-slate-950': open || isActive,
                          'text-slate-700 hover:bg-slate-100 hover:text-slate-950': !open && !isActive,
                          'cursor-not-allowed !text-slate-500': disabled
                        })}
                      >
                        {isLocked && (
                          <div className="absolute right-0 top-0 rounded-full bg-deep-orange p-1">
                            <Icon name="IconLock" className="size-3 text-white" />
                          </div>
                        )}
                        {!!showWarning && (
                          <div className="absolute right-0 top-0 rounded-md bg-deep-orange-50 p-1">
                            <Icon name="IconAlertTriangle" className="size-3 text-[#FF4200]" />
                          </div>
                        )}
                        {isBetaFeature && (
                          <div className="absolute -right-1 -top-1 z-10 flex items-center justify-center rounded-md bg-orange-100 px-1 py-0">
                            <span className="text-2xs font-medium text-[#FF4200]">Beta</span>
                          </div>
                        )}
                        {isLoading ? (
                          <Icon
                            name="IconLoader2"
                            className={classnames('animate-spin', {
                              [ACTION_BUTTON_SIZE_CLASSES[theme].medium]: size === 'medium' || size === 'base',
                              [ACTION_BUTTON_SIZE_CLASSES[theme].large]: size === 'large'
                            })}
                          />
                        ) : (
                          <Icon
                            name={icon}
                            className={classnames({
                              [ACTION_BUTTON_SIZE_CLASSES[theme].medium]: size === 'medium',
                              [ACTION_BUTTON_SIZE_CLASSES[theme].base]: size === 'base',
                              [ACTION_BUTTON_SIZE_CLASSES[theme].large]: size === 'large'
                            })}
                          />
                        )}
                        {size === 'medium' ? (
                          <div className="text-c3xs mt-1.5 text-2xs font-icons">{label}</div>
                        ) : size === 'large' ? (
                          <div className="text-c2xs mt-2 text-xs font-icons">{label}</div>
                        ) : (
                          <div
                            className={`${
                              labelPosition === 'left' ? 'right-14' : 'left-14'
                            } text-c2xs pointer-events-none absolute flex w-max -translate-x-2 items-center justify-center rounded bg-slate-900 p-1 px-2 text-xs font-icons text-white opacity-0 transition-all delay-100 duration-150 ease-in-out group-hover:translate-x-0 group-hover:opacity-100`}
                          >
                            <div
                              className={`absolute size-2 rotate-45 ${
                                labelPosition === 'left' ? 'right-[-3px]' : 'left-[-3px]'
                              } bg-slate-900`}
                            ></div>
                            <span>{label}</span>
                          </div>
                        )}
                      </button>
                    );
                    if (tooltip) {
                      return (
                        <TooltipV2
                          key={trackingId}
                          tooltipId={`${trackingId}-tooltip`}
                          tooltipElement={buttonElement}
                          tooltipContent={tooltip}
                          size="xsmall"
                          colorTheme="light"
                          place="left"
                          positionStrategy="fixed"
                        />
                      );
                    }
                    return buttonElement;
                  }
                )}
              </>
            )}
          </Popover>
        </div>
      </Transition>
    </div>
  );
}
